import {
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from 'react-hook-form'

import { exists } from './exists'
import { getByPath } from './getByPath'

/**
 * オブジェクトから指定されたパスの値に変換関数を適用した結果を返します。
 * オブジェクトやパスの値が null または undefined の場合はデフォルト値を返します。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @template U - 変換関数の戻り値の型。
 * @param obj - オブジェクト。
 * @param path - パス。
 * @param fn - 変換関数。
 * @param defaultValue - デフォルト値。
 * @returns オブジェクトのパスの値に変換関数を適用した結果。
 */
export function pathOrWith<T extends FieldValues, P extends FieldPath<T>, U>(
  obj: T | null | undefined,
  path: P,
  fn: (value: FieldPathValue<T, P>) => U,
  defaultValue: U,
): U

/**
 * オブジェクトから指定されたパスの値に変換関数を適用した結果を返します。
 * オブジェクトやパスの値が null または undefined の場合はデフォルト値を返します。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @template U - 変換関数の戻り値の型。
 * @param path - パス。
 * @param fn - 変換関数。
 * @param defaultValue - デフォルト値。
 * @returns オブジェクトを引数に取り、オブジェクトのパスの値に変換関数を適用した結果を返す関数。
 */
export function pathOrWith<T extends FieldValues, P extends FieldPath<T>, U>(
  path: P,
  fn: (value: FieldPathValue<T, P>) => U,
  defaultValue: U,
): (obj: T | null | undefined) => U

/**
 * @param args - 引数。
 * @returns 結果または部分適用された関数。
 */
export function pathOrWith<T extends FieldValues, P extends FieldPath<T>, U>(
  ...args:
    | [P, (value: FieldPathValue<T, P>) => U, U]
    | [T | null | undefined, P, (value: FieldPathValue<T, P>) => U, U]
): U | ((obj: T | null | undefined) => U) {
  const pathOrExistInner = (
    obj: T | null | undefined,
    path: P,
    fn: (value: FieldPathValue<T, P>) => U,
    defaultValue: U,
  ): U => {
    const result = getByPath(obj, path)

    return exists(result) ? fn(result) : defaultValue
  }

  if (args.length === 3) {
    return (obj: T | null | undefined) => pathOrExistInner(obj, ...args)
  }

  return pathOrExistInner(...args)
}
